import { LayoutGrid, Calendar, QrCode, Ticket, Users, MoreHorizontal, UserCircle, Video, MessageCircle, Image, ChevronRight, X } from "lucide-react";
import { useState } from "react";

const MOBILE_NAV_ITEMS = [
  { id: "overview", label: "OVERVIEW", icon: LayoutGrid },
  { id: "meetings", label: "MEETINGS", icon: Calendar },
  { id: "scan", label: "SCAN QR", icon: QrCode, isAction: true },
  { id: "booth-members", label: "MEMBERS", icon: Users },
  { id: "more", label: "MORE", icon: MoreHorizontal },
];

const MORE_NAV_ITEMS = [
  { id: "ticket", label: "MY TICKETS", icon: Ticket },
  { id: "exhibitor-profile", label: "Exhibitor Profile", icon: UserCircle },
  { id: "booth-materials", label: "Booth Materials", icon: Video },
  { id: "promo-banner", label: "Promotional Banner", icon: Image },
  { id: "leads", label: "Leads", icon: Users },
  { id: "inquiries", label: "Inquiries", icon: MessageCircle },
];

function MobileBottomNav({ activeTab, onTabChange, features = [] }) {
  const [showMore, setShowMore] = useState(false);

  // Filter mobile nav items based on category features
  const filteredNavItems = MOBILE_NAV_ITEMS.filter((item) => {
    if (item.id === "more") return true;
    
    // Scan QR is tied to lead_scanning feature
    const featureKey = item.id === "scan" ? "lead_scanning" : item.id;
    const feature = features.find(f => f.key === featureKey);

    if (feature) {
        return feature.enabled === 1 && feature.visible === 1;
    }

    return true; 
  });

  const filteredMoreItems = MORE_NAV_ITEMS.filter((item) => {
    if (item.id === "booth-materials") {
      const v = features.find(f => f.key === "videos");
      const c = features.find(f => f.key === "collateral");
      return (v?.enabled === 1 && v?.visible === 1) || (c?.enabled === 1 && c?.visible === 1);
    }

    const featureKeyMap = {
      "leads": "lead_scanning",
      "promo-banner": "promotional_banner"
    };

    const featureKey = featureKeyMap[item.id] || item.id;
    const feature = features.find(f => f.key === featureKey);

    if (feature) {
        return feature.enabled === 1 && feature.visible === 1;
    }

    return true; 
  });

  return (
    <div className="md:hidden w-full fixed bottom-0 left-0 z-[120] flex justify-center items-center pointer-events-none">
      {showMore && (
        <div
          className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-sm flex items-end justify-center pointer-events-auto pb-[60px]"
          onClick={() => setShowMore(false)}
        >
          <div
            className="bg-white w-full max-w-lg rounded-t-[2.5rem] rounded-b-none p-6 pt-8 shadow-2xl animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6 px-2">
              <h3 className="section-heading">EXTENDED MENU</h3>
              <button
                onClick={() => setShowMore(false)}
                className="bg-gray-100 p-2 rounded-full text-gray-500"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {filteredMoreItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    onTabChange(item.id);
                    setShowMore(false);
                  }}
                  className={`flex flex-col items-center gap-2 p-4 rounded-3xl transition-all ${
                    activeTab === item.id
                      ? "bg-primary-color/10 text-primary-color"
                      : "bg-gray-50 text-gray-600 active:scale-95"
                  }`}
                >
                  <item.icon
                    size={24}
                    strokeWidth={activeTab === item.id ? 2.5 : 2}
                  />
                  <span className="text-[10px] font-pj font-bold text-center leading-tight">
                    {item.label}
                  </span>
                </button>
              ))}
            </div>

            {/* Added a safe area spacer for iOS */}
            <div className="h-6 w-full"></div>
          </div>
        </div>
      )}

      <nav 
        className="bg-white/95 backdrop-blur-3xl px-2 py-2 rounded-xs shadow-[0_12px_48px_-12px_rgba(0,0,0,0.12)] border border-gray-100 grid items-center w-full max-w-lg pointer-events-auto z-[130]"
        style={{ gridTemplateColumns: `repeat(${filteredNavItems.length}, minmax(0, 1fr))` }}
      >
        {filteredNavItems.map((item) => {
          const isActive = activeTab === item.id;
          const isMoreActive = item.id === "more" && showMore;
          const Icon = isMoreActive ? X : item.icon;

          if (item.isAction) {
            return (
              <button
                key={item.id}
                onClick={() => onTabChange("leads", { state: { openScanner: true } })}
                className="flex flex-col items-center gap-1 group -mt-11"
              >
                <div className="w-16 h-16 bg-primary-color rounded-[1.25rem] flex items-center justify-center text-white shadow-[0_16px_32px_-8px_rgba(67,94,112,0.3)] border-[5px] border-white transition-all transform active:scale-95 group-hover:scale-105 duration-300">
                  <Icon size={28} strokeWidth={2.5} />
                </div>
                <span className="text-[10px] font-pj font-black text-primary-color tracking-tight mt-1 whitespace-nowrap">
                  {item.label}
                </span>
              </button>
            );
          }

          return (
            <button
              key={item.id}
              onClick={() => {
                if (item.id === "more") {
                  setShowMore(!showMore);
                } else {
                  onTabChange(item.id);
                  setShowMore(false);
                }
              }}
              className={`flex flex-col items-center justify-center px-2 py-2 rounded-[1.75rem] transition-all duration-300 gap-1.5 ${
                isActive || isMoreActive
                  ? "text-primary-color scale-105"
                  : "text-gray-400 hover:text-gray-500"
              }`}
            >
              <Icon
                size={22}
                className={`transition-transform duration-300 ${isActive ? "scale-110" : ""}`}
                strokeWidth={isActive ? 2.5 : 2}
              />
              <span
                className={`text-[9.5px] font-pj font-extrabold tracking-tight whitespace-nowrap ${
                  isActive ? "text-primary-color" : "text-gray-400"
                }`}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>

      {/* Removed old overlay position */}
    </div>
  );
}

export default MobileBottomNav;
